import React, { useState, useEffect } from "react";
import { Card, Button, Form, Alert, Spinner, Row, Col } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import "./download-reports.css";

interface ProjectOption {
  job_number: string;
}

interface ReportConfig {
  id: string;
  name: string;
  endpoint: string;
  filename: (date: string, projectNo: string) => string;
  requiresDate: boolean;
  description: string;
  category: "monthly" | "project" | "general";
}

interface DownloadReportsProps {
  apiKey: string;
  initialProject?: string;
  initialDate?: string;
}

const DownloadReports: React.FC<DownloadReportsProps> = ({
  apiKey,
  initialProject,
  initialDate,
}) => {
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>(
    initialProject || ""
  );
  const [selectedDate, setSelectedDate] = useState<string>(
    initialDate || new Date().toISOString().slice(0, 7)
  );
  const [downloadingReports, setDownloadingReports] = useState<Set<string>>(
    new Set()
  );
  const [projectsLoading, setProjectsLoading] = useState<boolean>(true);

  // Report configurations
  const reportConfigs: ReportConfig[] = [
    {
      id: "table3",
      name: "Table 3",
      endpoint: "reports/downloadTable3",
      filename: (date, projectNo) => `${date}_${projectNo}_table3.xlsx`,
      requiresDate: false,
      description: "Project summary and basic information",
      category: "project",
    },
    {
      id: "table4",
      name: "Table 4",
      endpoint: "reports/downloadTable4?type=4",
      filename: (date, projectNo) => `${date}_${projectNo}_table4.xlsx`,
      requiresDate: true,
      description: "Monthly expenditure report",
      category: "monthly",
    },
    {
      id: "table4a",
      name: "Table 4a",
      endpoint: "reports/downloadTable4?type=4a",
      filename: (date, projectNo) => `${date}_${projectNo}_table4a.xlsx`,
      requiresDate: true,
      description: "Detailed expenditure breakdown",
      category: "monthly",
    },
    {
      id: "table4b",
      name: "Table 4b",
      endpoint: "reports/downloadTable4?type=4b",
      filename: (date, projectNo) => `${date}_${projectNo}_table4b.xlsx`,
      requiresDate: true,
      description: "Resource allocation report",
      category: "monthly",
    },
    {
      id: "table5",
      name: "Table 5",
      endpoint: "reports/downloadTable5",
      filename: () => `table5.xlsx`,
      requiresDate: false,
      description: "General summary report",
      category: "general",
    },
    {
      id: "table6",
      name: "Table 6",
      endpoint: "reports/downloadTable6",
      filename: (date, projectNo) => `${projectNo}_table6.xlsx`,
      requiresDate: false,
      description:
        "Cost code analysis with budget vs actual costs and variance",
      category: "project",
    },
    {
      id: "figure1",
      name: "Figure 1",
      endpoint: "reports/downloadFigure1",
      filename: (date, projectNo) => `${projectNo}_figure1.xlsx`,
      requiresDate: false,
      description:
        "Financial expenditure comparison: forecasted vs actual monthly and cumulative expenditure with percentage differences",
      category: "project",
    },
  ];

  // Load projects on component mount
  useEffect(() => {
    if (apiKey) {
      loadProjects();
    } else {
      setProjectsLoading(false);
      setProjects([]);
      setSelectedProject("");
    }
  }, [apiKey]);

  const loadProjects = async () => {
    if (!apiKey) {
      setProjectsLoading(false);
      return;
    }

    try {
      setProjectsLoading(true);
      // Try to get projects from any available endpoint
      let response;
      try {
        response = await axios.get(
          `${process.env.REACT_APP_API_URL}estimated-budget-ceiling/projects`,
          {
            headers: { Authorization: apiKey },
            validateStatus: () => true,
          }
        );
      } catch {
        // Fallback to hardcoded projects if API fails
        setProjects([{ job_number: "B210841" }, { job_number: "B240361" }]);
        if (!selectedProject) {
          setSelectedProject("B210841");
        }
        setProjectsLoading(false);
        return;
      }

      if (response.status === 200) {
        setProjects(response.data);
        if (response.data.length > 0 && !selectedProject) {
          setSelectedProject(response.data[0].job_number);
        }
      } else {
        // Fallback to hardcoded projects
        setProjects([{ job_number: "B210841" }, { job_number: "B240361" }]);
        if (!selectedProject) {
          setSelectedProject("B210841");
        }
      }
    } catch (error) {
      console.error("Error loading projects:", error);
      // Fallback to hardcoded projects
      setProjects([{ job_number: "B210841" }, { job_number: "B240361" }]);
      if (!selectedProject) {
        setSelectedProject("B210841");
      }
    } finally {
      setProjectsLoading(false);
    }
  };

  const downloadReport = async (config: ReportConfig) => {
    if (!apiKey || !selectedProject) {
      Swal.fire({
        title: "Missing Information",
        text: "Please ensure you are authenticated and have selected a project.",
        icon: "warning",
      });
      return;
    }

    const reportId = config.id;
    setDownloadingReports((prev) => new Set(prev).add(reportId));

    try {
      // Build URL with appropriate parameters
      let url = `${process.env.REACT_APP_API_URL}${config.endpoint}`;
      const params = new URLSearchParams();

      // Add project parameter
      params.append("projectno", selectedProject);

      // Add date parameters if required
      if (config.requiresDate) {
        const [year, month] = selectedDate.split("-");
        params.append("year", year);
        params.append("month", month);
      }

      // Append parameters to URL
      if (params.toString()) {
        url += (url.includes("?") ? "&" : "?") + params.toString();
      }

      const response = await axios.get(url, {
        headers: { Authorization: apiKey },
        responseType: "blob",
        validateStatus: () => true,
      });

      if (response.status === 401) {
        Swal.fire({
          title: "Session expired",
          text: "Please login again",
          icon: "warning",
        });
        return;
      }

      if (response.status >= 500) {
        Swal.fire({
          title: "Server Error",
          text: "An unexpected error occurred while generating the report.",
          icon: "error",
        });
        return;
      }

      if (response.status >= 400) {
        Swal.fire({
          title: "Report Generation Failed",
          text: "Unable to generate the requested report. Please check your parameters.",
          icon: "error",
        });
        return;
      }

      // Success - download the file
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = config.filename(selectedDate, selectedProject);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      // Show success message
      Swal.fire({
        title: "Download Complete",
        text: `${config.name} has been downloaded successfully.`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error downloading report:", error);
      Swal.fire({
        title: "Download Failed",
        text: "An error occurred while downloading the report.",
        icon: "error",
      });
    } finally {
      setDownloadingReports((prev) => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
    }
  };

  const groupedReports = reportConfigs.reduce((acc, config) => {
    if (!acc[config.category]) {
      acc[config.category] = [];
    }
    acc[config.category].push(config);
    return acc;
  }, {} as Record<string, ReportConfig[]>);

  const getCategoryTitle = (category: string) => {
    switch (category) {
      case "monthly":
        return "Monthly Reports";
      case "project":
        return "Project Reports";
      case "general":
        return "General Reports";
      default:
        return "Other Reports";
    }
  };

  const getCategoryDescription = (category: string) => {
    switch (category) {
      case "monthly":
        return "Reports that require a specific date and project selection";
      case "project":
        return "Reports specific to the selected project";
      case "general":
        return "General reports not tied to specific projects or dates";
      default:
        return "";
    }
  };

  if (!apiKey) {
    return (
      <Card className="download-reports-container">
        <Card.Body className="text-center">
          <div className="p-4">
            <h5>Authentication Required</h5>
            <p className="text-muted">
              Please authenticate to access report downloads.
            </p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (projectsLoading) {
    return (
      <Card className="download-reports-container">
        <Card.Body className="text-center">
          <Spinner animation="border" />
          <div className="mt-2">Loading projects...</div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="download-reports-container">
      <Card.Header>
        <h5>Download Reports</h5>
      </Card.Header>

      <Card.Body>
        <div className="report-controls">
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Project:</Form.Label>
                <Form.Select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="project-selector">
                  {projects.map((project) => (
                    <option key={project.job_number} value={project.job_number}>
                      {project.job_number}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Date:</Form.Label>
                <Form.Control
                  type="month"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="date-selector"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>

        <div className="reports-grid">
          {Object.entries(groupedReports).map(([category, configs]) => (
            <div key={category} className="report-category">
              <h6 className="category-title">{getCategoryTitle(category)}</h6>
              <p className="category-description">
                {getCategoryDescription(category)}
              </p>

              <Row>
                {configs.map((config) => (
                  <Col key={config.id} md={6} lg={4} className="mb-3">
                    <Card className="report-card h-100">
                      <Card.Body className="d-flex flex-column">
                        <h6 className="report-title">{config.name}</h6>
                        <p className="report-description flex-grow-1">
                          {config.description}
                        </p>
                        <div className="report-info">
                          {config.requiresDate && (
                            <small className="text-muted d-block">
                              📅 Requires date selection
                            </small>
                          )}
                          <small className="text-muted d-block">
                            📁 Project: {selectedProject}
                          </small>
                        </div>
                        <Button
                          variant="primary"
                          onClick={() => downloadReport(config)}
                          disabled={
                            downloadingReports.has(config.id) ||
                            !selectedProject ||
                            (config.requiresDate && !selectedDate)
                          }
                          className="mt-2">
                          {downloadingReports.has(config.id) ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Downloading...
                            </>
                          ) : (
                            <>📥 Download {config.name}</>
                          )}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
};

export default DownloadReports;
